import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { sb } from "@/lib/sb";

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_KEY = (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

const SESSION_KEY = "hc_session_id";
function getSessionId(): string {
  if (typeof window === "undefined") return "";
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function finalize(id: string, startedAt: number, useBeacon = false) {
  const seconds = Math.round((Date.now() - startedAt) / 1000);
  const args = {
    p_id: id,
    p_session_id: getSessionId(),
    p_seconds: seconds,
    p_is_bounce: seconds < 10,
  };
  if (useBeacon && SUPABASE_URL && SUPABASE_KEY) {
    // sendBeacon / keepalive-fetch survives page unload.
    const url = `${SUPABASE_URL}/rest/v1/rpc/finalize_page_analytics`;
    const body = JSON.stringify(args);
    try {
      if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
        const blob = new Blob([body], { type: "application/json" });
        // Beacon can't set headers; fall through to keepalive-fetch which can.
        void blob;
      }
      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${SUPABASE_KEY}`,
        },
        body,
      }).catch(() => {});
      return Promise.resolve();
    } catch {
      /* fall through */
    }
  }
  return sb.rpc("finalize_page_analytics", args);
}

export function useAnalyticsTracker(skip: boolean) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const currentRef = useRef<{ id: string; startedAt: number } | null>(null);

  // Record the pageview on every path change.
  useEffect(() => {
    if (skip || typeof window === "undefined") return;

    // Finalize the previous pageview (SPA navigation).
    const prev = currentRef.current;
    if (prev) {
      finalize(prev.id, prev.startedAt).then(() => {});
    }

    const session_id = getSessionId();
    const newId = crypto.randomUUID();
    let cancelled = false;
    const doInsert = () => {
      if (cancelled) return;
      sb.from("page_analytics")
        .insert({
          id: newId,
          path: pathname,
          session_id,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent?.slice(0, 500) ?? null,
        })
        .then(({ error }: { error: unknown }) => {
          if (!error) currentRef.current = { id: newId, startedAt: Date.now() };
          else console.warn("page_analytics insert failed", error);
        });
    };
    const w = window as any;
    const handle = w.requestIdleCallback
      ? w.requestIdleCallback(doInsert, { timeout: 2500 })
      : window.setTimeout(doInsert, 800);
    return () => {
      cancelled = true;
      if (w.cancelIdleCallback && w.requestIdleCallback) w.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, [pathname, skip]);

  // Finalize when the tab is hidden or unloaded — otherwise single-page
  // visits never record time_on_page_seconds / is_bounce.
  useEffect(() => {
    if (skip || typeof window === "undefined") return;
    const flush = () => {
      const cur = currentRef.current;
      if (!cur) return;
      finalize(cur.id, cur.startedAt, true);
      currentRef.current = null;
    };
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [skip]);
}
